import { Button, FieldLabel, Loader, OdinIcon } from '@myosh/odin-components';
import clsx from 'clsx';
import { cloneDeep } from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { RiskMatrixData, RiskMatrixDataItem } from '../../../@types/risk-matrix-field';
import {
  useCreateNewMatrixConsequenceMutation,
  useCreateNewMatrixLikelyhoodMutation,
  useRiskRatingMatrixConfigQuery,
} from '../../../redux/services/risk-matrix';
import PrimaryViewActionButton from '../../common/primary-view-action-button';
import RiskRatingModal from './rating-modal.component';
import type { RiskMatrixHederValue } from './risk-headers-modal.component';
import RiskHeadersModal from './risk-headers-modal.component';
import {
  additionalRiskCellItems,
  additionalRiskLevelCaptions,
  riskMatrixCellsSorted,
  uniqueRiskMatrixColumns,
} from './risk-rating-functions';
import RiskRatingMatrix from './risk-rating-matrix.component';

interface RiskMatrixAdminProps {
  label?: string;
  riskMatrixId: number;
  readOnly?: boolean;
  saveMatrix?: () => void;
}

const RiskMatrixAdmin = ({ riskMatrixId, label, readOnly, saveMatrix }: RiskMatrixAdminProps) => {
  const { t } = useTranslation();

  const [riskMatrixCells, setRiskMatrixCells] = useState<RiskMatrixData[]>([]);
  const [selectedRiskMatrixCell, setSelectedRiskMatrixCell] = useState<RiskMatrixDataItem>();
  const [selectedRiskMatrixHeader, setSelectedRiskMatrixHeader] = useState<RiskMatrixHederValue>();
  const { data: riskMatrixData, isFetching } = useRiskRatingMatrixConfigQuery(riskMatrixId, {
    skip: riskMatrixId === undefined,
  });
  const [createNewLikelihood] = useCreateNewMatrixLikelyhoodMutation();
  const [createNewConsequence] = useCreateNewMatrixConsequenceMutation();

  useEffect(() => {
    // The first item is a header row
    const cellPlaceholder = new Map<string, RiskMatrixData>();
    //1:1 cell should be empty
    cellPlaceholder.set('header', [{}]);

    const initialRiskLevels: Record<string, RiskMatrixDataItem> = {};

    if (riskMatrixData && riskMatrixData.length > 0) {
      const matrixDataByLikelihood = cloneDeep(riskMatrixData).sort(
        (riskMatrix1, riskMatrix2) => (riskMatrix1.likelihood.position ?? 0) - (riskMatrix2.likelihood.position ?? 0)
      );
      for (const riskMatrixElement of matrixDataByLikelihood) {
        const riskLevelKey = String(riskMatrixElement?.riskLevel?.category) as string;
        if (riskLevelKey) {
          initialRiskLevels[riskLevelKey] = riskMatrixElement.riskLevel;
        }

        const likelihoodRowKey =
          (riskMatrixElement?.likelihood?.caption?.translations[0].value as string) +
          String(riskMatrixElement?.likelihood?.id);

        const consequenceColumns = uniqueRiskMatrixColumns(cellPlaceholder.get('header') ?? [], riskMatrixElement);
        const riskCellItems = additionalRiskCellItems(riskMatrixElement);
        const riskLevelCaptions = additionalRiskLevelCaptions(riskMatrixElement);

        const riskLevelProperties = {
          ratingId: riskCellItems?.ratingId,
          rating: riskCellItems?.rating,
          category: riskCellItems?.category,
          consequencePosition: riskMatrixElement.consequence.position,
          cellId: riskMatrixElement.id,
          riskLevelCaptions: {
            ...riskMatrixElement.riskLevel.riskLevelCaptions,
            consequenceCaption: riskLevelCaptions.consequenceCaption,
            consequenceDescription: riskLevelCaptions.consequenceDescription,
            likelihoodCaption: riskLevelCaptions.likelihoodCaption,
            likelihoodDescription: riskLevelCaptions.likelihoodDescription,
          },
        };

        if (!consequenceColumns) {
          cellPlaceholder.set('header', [...(cellPlaceholder.get('header') ?? []), riskMatrixElement.consequence]);
        }

        if (!cellPlaceholder.has(likelihoodRowKey)) {
          cellPlaceholder.set(likelihoodRowKey, [
            riskMatrixElement.likelihood,
            {
              ...riskMatrixElement.riskLevel,
              ...riskLevelProperties,
            },
          ]);
        } else {
          cellPlaceholder.set(likelihoodRowKey, [
            ...(cellPlaceholder.get(likelihoodRowKey) ?? []),
            {
              ...riskMatrixElement.riskLevel,
              ...riskLevelProperties,
            },
          ]);
        }
      }
      const transformedRiskMatrixCells = riskMatrixCellsSorted(cellPlaceholder);

      setRiskMatrixCells(transformedRiskMatrixCells);
    }
  }, [riskMatrixData]);

  const onRatingCellClick = (riskMatrixCellData: RiskMatrixDataItem) => {
    setSelectedRiskMatrixCell(riskMatrixCellData);
  };

  const handleAddConsequence = () => {
    if (riskMatrixId) {
      createNewConsequence(riskMatrixId);
    }
  };

  const handleAddLikelihood = () => {
    if (riskMatrixId) {
      createNewLikelihood(riskMatrixId);
    }
  };

  const handleRiskRatingModalClose = () => {
    setSelectedRiskMatrixCell(undefined);
  };

  const handleRiskHeadersModalClose = () => {
    setSelectedRiskMatrixHeader(undefined);
  };

  const handleConsequenceValueChange = (riskMatrixCell: RiskMatrixDataItem) => {
    setSelectedRiskMatrixHeader({ type: 'consequence', value: riskMatrixCell });
  };

  const handleLikelyhoodValueChange = (riskMatrixCell: RiskMatrixDataItem) => {
    setSelectedRiskMatrixHeader({ type: 'likelihood', value: riskMatrixCell });
  };

  const riskMatrixStyles = clsx('min-w-[200px] min-h-[80px] overflow-auto custom-visible-scroll bg-mono-1 p-2', {
    'pointer-events-none select-none opacity-70 cursor-not-allowed': readOnly,
  });

  const addNewRiskMatrixStyles = clsx('w-full ml-1 mb-1', {
    'pointer-events-none select-none opacity-50 cursor-not-allowed': readOnly,
  });

  const handleCreateRiskMAtrix = () => {
    if (!riskMatrixId) {
      saveMatrix?.();
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {label && (
        <div className="flex">
          <FieldLabel label={label} />
        </div>
      )}

      {!riskMatrixId && (
        <div className={addNewRiskMatrixStyles}>
          <PrimaryViewActionButton
            onClick={handleCreateRiskMAtrix}
          >{`${t('add')} ${t('risk-matrix')}`}</PrimaryViewActionButton>
        </div>
      )}

      {riskMatrixId && (
        <>
          {isFetching ? (
            <div className="mx-auto flex h-52 w-96 flex-col items-center justify-center">
              <Loader />
            </div>
          ) : (
            <>
              <div className="flex gap-4">
                <Button type="text" icon={<OdinIcon icon="Plus" />} onClick={handleAddConsequence} disabled={readOnly}>
                  {`${t('add')} ${t('consequence')}`}
                </Button>
                <Button type="text" icon={<OdinIcon icon="Plus" />} onClick={handleAddLikelihood} disabled={readOnly}>
                  {`${t('add')} ${t('likelihood')}`}
                </Button>
              </div>
              {riskMatrixCells.length > 0 && (
                <div className={riskMatrixStyles}>
                  <RiskRatingMatrix
                    riskMatrixData={riskMatrixCells}
                    onRatingCellClick={onRatingCellClick}
                    handleConsequenceValueChange={handleConsequenceValueChange}
                    handleLikelyhoodValueChange={handleLikelyhoodValueChange}
                    isAdminMode
                  />
                </div>
              )}
            </>
          )}
        </>
      )}
      {Boolean(selectedRiskMatrixCell) && (
        <RiskRatingModal
          riskMatrixId={riskMatrixId}
          selectedRiskMatrixCell={selectedRiskMatrixCell}
          visible={Boolean(selectedRiskMatrixCell)}
          handleModalClose={handleRiskRatingModalClose}
        />
      )}
      {Boolean(selectedRiskMatrixHeader) && (
        <RiskHeadersModal
          selectedRiskMatrixHeader={selectedRiskMatrixHeader}
          visible={Boolean(selectedRiskMatrixHeader)}
          handleModalClose={handleRiskHeadersModalClose}
        />
      )}
    </div>
  );
};

export default RiskMatrixAdmin;
